/** Client-side protocol for the reviewed Auth UID to users-document binding flow.
 * The injected transport owns authentication/routing; this module never reads identity
 * from Firebase or sends actor, project, or server timestamp fields.
 */
export interface AccountBindingSelection {
  authUid: string;
  formerUserId: string | null;
  targetUserId: string;
  targetPersonId: number;
  frozenInventorySha256: string;
  expectedBindingRevision?: number;
  expectedBindingOperationDigest?: string;
}

export type AccountBindingOutcome = 'approved' | 'present' | 'applied';
export interface AccountBindingReviewController {
  prepare(): Promise<{ taskId: string; operationKey: string; planSha256: string }>;
  preview(): Promise<{ taskId: string; operationKey: string; planSha256: string; input: unknown; profiles: unknown }>;
  approve(): Promise<{ taskId: string; planSha256: string; outcome: 'approved' | 'present' }>;
  apply(): Promise<{ taskId: string; planSha256: string; outcome: 'applied' | 'present' }>;
  getState(): Readonly<AccountBindingReviewState>;
}

export interface AccountBindingReviewState {
  taskId?: string;
  preparedPlanSha256?: string;
  previewPlanSha256?: string;
  previewInput?: unknown;
  previewProfiles?: unknown;
  approvedPlanSha256?: string;
  applied?: boolean;
}

type Transport = (payload: unknown) => Promise<unknown>;
type RecordValue = Record<string, unknown>;

const isRecord = (value: unknown): value is RecordValue => !!value && typeof value === 'object' && !Array.isArray(value);
const validId = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const validHash = (value: unknown): value is string => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const copyJsonValue = <T>(value: T): T => {
  const encoded = JSON.stringify(value);
  if (encoded === undefined) throw new Error('Account-binding review data is not JSON-safe.');
  return JSON.parse(encoded) as T;
};
const response = (value: unknown, phase: string): RecordValue => {
  if (!isRecord(value) || value.phase !== phase) throw new Error(`Invalid account-binding ${phase} response.`);
  return value;
};

/** Create a single review session. Reuse this controller for retries after transport errors. */
export function createAccountBindingReviewController(
  transport: Transport,
  operationKey: string,
  selection: AccountBindingSelection
): AccountBindingReviewController {
  const revision = selection?.expectedBindingRevision ?? 0;
  const operationDigest = selection?.expectedBindingOperationDigest;
  if (typeof operationKey !== 'string' || !/^[a-zA-Z0-9_-]{8,128}$/.test(operationKey)
    || !isRecord(selection) || !validId(selection.authUid)
    || !validId(selection.targetUserId) || !Number.isSafeInteger(selection.targetPersonId)
    || Number(selection.targetPersonId) <= 0
    || !validHash(selection.frozenInventorySha256)
    || (selection.formerUserId !== null && !validId(selection.formerUserId))
    || selection.formerUserId === selection.targetUserId
    || !Number.isSafeInteger(revision) || Number(revision) < 0
    || Number(revision) >= Number.MAX_SAFE_INTEGER
    || (Number(revision) === 0 ? operationDigest !== undefined : !validHash(operationDigest))) {
    throw new Error('Invalid account-binding review selection.');
  }
  // Keep a private value copy, so later mutations to the caller's object cannot alter retries.
  const frozenSelection: AccountBindingSelection = {
    authUid: selection.authUid,
    formerUserId: selection.formerUserId,
    targetUserId: selection.targetUserId,
    targetPersonId: selection.targetPersonId,
    frozenInventorySha256: selection.frozenInventorySha256,
    ...(selection.expectedBindingRevision !== undefined
      ? { expectedBindingRevision: selection.expectedBindingRevision } : {}),
    ...(selection.expectedBindingOperationDigest !== undefined
      ? { expectedBindingOperationDigest: selection.expectedBindingOperationDigest } : {}),
  };
  const state: AccountBindingReviewState = {};

  const requireTask = (): string => {
    if (!state.taskId) throw new Error('Prepare the account-binding review first.');
    return state.taskId;
  };

  return {
    async prepare() {
      const result = response(await transport({ phase: 'prepare', operationKey, selection: { ...frozenSelection } }), 'prepare');
      if (!validId(result.taskId) || result.operationKey !== operationKey || !validHash(result.planSha256)) {
        throw new Error('Account-binding prepare response does not match this operation.');
      }
      if (state.taskId && state.taskId !== result.taskId) throw new Error('Account-binding task changed for this operation.');
      if (state.preparedPlanSha256 && state.preparedPlanSha256 !== result.planSha256) {
        throw new Error('Account-binding prepared plan changed for this operation.');
      }
      state.taskId = result.taskId;
      state.preparedPlanSha256 = result.planSha256;
      return { taskId: result.taskId, operationKey, planSha256: result.planSha256 };
    },

    async preview() {
      const taskId = requireTask();
      const result = response(await transport({ phase: 'preview', taskId }), 'preview');
      if (result.taskId !== taskId || result.operationKey !== operationKey || !validHash(result.planSha256)
        || result.validation !== 'current-state' || result.readinessVerified !== true || result.applied !== false
        || !Object.prototype.hasOwnProperty.call(result, 'input')
        || !Object.prototype.hasOwnProperty.call(result, 'profiles')) {
        throw new Error('Account-binding preview response is stale or incomplete.');
      }
      // A changed server plan invalidates any earlier approval before it can be applied.
      if (result.planSha256 !== state.preparedPlanSha256) {
        state.previewPlanSha256 = undefined;
        state.previewInput = undefined;
        state.previewProfiles = undefined;
        state.approvedPlanSha256 = undefined;
        throw new Error('Account-binding preview plan changed; prepare a new operation.');
      }
      const input = copyJsonValue(result.input);
      const profiles = copyJsonValue(result.profiles);
      state.previewPlanSha256 = result.planSha256;
      state.previewInput = input;
      state.previewProfiles = profiles;
      state.applied = false;
      return { taskId, operationKey, planSha256: result.planSha256,
        input: copyJsonValue(input), profiles: copyJsonValue(profiles) };
    },

    async approve() {
      const taskId = requireTask();
      const planSha256 = state.previewPlanSha256;
      if (!planSha256) throw new Error('A successful current-state preview is required before approval.');
      const result = response(await transport({ phase: 'approve', taskId, approvedPlanSha256: planSha256 }), 'approve');
      if (result.taskId !== taskId || result.planSha256 !== planSha256
        || (result.outcome !== 'approved' && result.outcome !== 'present')) {
        throw new Error('Account-binding approval response does not match the reviewed plan.');
      }
      state.approvedPlanSha256 = planSha256;
      return { taskId, planSha256, outcome: result.outcome };
    },

    async apply() {
      const taskId = requireTask();
      const planSha256 = state.approvedPlanSha256;
      if (!planSha256) throw new Error('A successful approval is required before apply.');
      if (state.applied) throw new Error('This account-binding operation is already complete.');
      const result = response(await transport({ phase: 'apply', taskId, approvedPlanSha256: planSha256 }), 'apply');
      if (result.taskId !== taskId || result.planSha256 !== planSha256
        || (result.outcome !== 'applied' && result.outcome !== 'present')) {
        throw new Error('Account-binding apply response does not match the approved plan.');
      }
      state.applied = true;
      return { taskId, planSha256, outcome: result.outcome };
    },

    getState() { return copyJsonValue(state); },
  };
}
